import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { Container, Form, Row, Col } from 'react-bootstrap';
import zhTW from 'date-fns/locale/zh-TW';
import api from '../../services/api';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CoachScheduleCalendar.css';
import { 
  Box, 
  Typography, 
  Container as MuiContainer, 
  Paper, 
  IconButton, 
  Tooltip,
  Fade 
} from '@mui/material';
import { 
  Home as HomeIcon 
} from '@mui/icons-material';

const locales = {
  'zh-TW': zhTW,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const statusColors = {
  OPEN: '#28a745',     // 綠色
  BOOKED: '#dc3545',   // 紅色
  RESERVED: '#ffc107', // 黃色
  EMPTY: '#6c757d',   // 灰色
  MOVING: '#265985',   // 深藍色
};

const statusLabels = {
  OPEN: '可預約',
  BOOKED: '已有課',
  RESERVED: '預約中',
  EMPTY: '保留',
  MOVING: '移動中',
};

const CustomToolbar = (toolbar) => {
  const navigate = (action) => {
    toolbar.onNavigate(action);
  };

  return (
    <div className="rbc-toolbar">
      <span className="rbc-btn-group">
        <button type="button" onClick={() => navigate('PREV')}>
          上個月
        </button>
        <button type="button" onClick={() => navigate('NEXT')}>
          下個月
        </button>
      </span>
      <span className="rbc-toolbar-label">
        {format(toolbar.date, 'yyyy年MM月', { locale: zhTW })}
      </span>
    </div>
  );
};

const CoachScheduleCalendar = () => {
  const [schedules, setSchedules] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState('');

  // 獲取教練列表
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const response = await api.get('/coaches/');
        setCoaches(response.data);
      } catch (error) {
        console.error('Error fetching coaches:', error);
      }
    };
    fetchCoaches();
  }, []);

  // 獲取課程時間表
  useEffect(() => {
    const fetchSchedules = async () => {
      if (!selectedCoach) return;
      
      try {
        console.log("Selected coach:", selectedCoach);
        const response = await api.get(`/schedules/public_schedules/?coach=${selectedCoach}`);
        console.log("Raw response data:", response.data);  // 檢查原始數據
        
        const formattedSchedules = response.data.map(schedule => {
          // 解析時段
          const timeSlotMap = {
            'MORNING': ['09:00', '12:00'],
            'AFTERNOON': ['14:00', '17:00'],
            'EVENING': ['19:00', '22:00']
          };
          
          const [startTime, endTime] = timeSlotMap[schedule.time_slot] || ['00:00', '00:00'];
          
          // 組合日期和時間
          const startDate = new Date(`${schedule.date}T${startTime}:00`);
          const endDate = new Date(`${schedule.date}T${endTime}:00`);

          return {
            id: schedule.id,
            title: schedule.location, //`${schedule.coach_name} - ${schedule.time_slot_display}`,
            start: startDate,
            end: endDate,
            status: schedule.status,
            coach_name: schedule.coach_name,
            time_slot_display: schedule.time_slot_display,
          };
        });

        console.log("Formatted schedules:", formattedSchedules);
        setSchedules(formattedSchedules);
      } catch (error) {
        console.error('Error fetching schedules:', error);
      }
    };

    fetchSchedules();
  }, [selectedCoach]);

  // 自定義事件樣式
  const eventStyleGetter = (event) => {
    let style = {
      backgroundColor: '#3174ad',
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: '0',
      display: 'block',
      padding: '2px 5px',
    };

    switch (event.status) {
      case 'OPEN':
        style.backgroundColor = '#28a745';
        break;
      case 'RESERVED':
        style.backgroundColor = '#ffc107';
        break;
      case 'BOOKED':
        style.backgroundColor = '#dc3545';
        break;
      default:
        break;
    }

    return {
      style,
    };
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 4
    }}>
      <MuiContainer maxWidth="lg">
        {/* 返回首頁按鈕 */}
        <Fade in timeout={500}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
            <Tooltip title="返回首頁 ASA-滑雪裝忙協會">
              <IconButton
                href="/"
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                  }
                }}
              >
                <HomeIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Fade>

        {/* 現有的日曆內容 */}
        <Paper 
          elevation={8}
          sx={{ 
            p: 4,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.3)'
          }}
        >
          <h2 className="mb-4">教練行程</h2>
          
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>選擇教練</Form.Label>
                <Form.Select
                  value={selectedCoach}
                  onChange={(e) => setSelectedCoach(e.target.value)}
                >
                  <option value="">請選擇教練</option>
                  {coaches.map((coach) => (
                    <option key={coach.id} value={coach.id}>
                      {coach.username}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* status label */}
          <div className="schedule-legend">
            {Object.entries(statusLabels).map(([key, label]) => (
              <div key={key} className="legend-item">
                <span 
                  className="legend-color" 
                  style={{ backgroundColor: statusColors[key] }}
                ></span>
                <span>{label}</span>
              </div>
            ))}
          </div>

          <div className="calendar-container">
            <Calendar
              localizer={localizer}
              events={schedules}
              startAccessor="start"
              endAccessor="end"
              views={['month']}
              defaultView="month"
              eventPropGetter={eventStyleGetter}
              tooltipAccessor={(event) => 
                `${event.coach_name}\n${event.time_slot_display}\n${
                  event.status === 'OPEN' ? '可預約' :
                  event.status === 'RESERVED' ? '已預約' :
                  event.status === 'BOOKED' ? '已確認' : ''
                }`
              }
              messages={{
                month: '月',
                previous: '上個月',
                next: '下個月',
              }}
              components={{
                toolbar: CustomToolbar
              }}
            />
          </div>
        </Paper>
      </MuiContainer>
    </Box>
  );
};

export default CoachScheduleCalendar; 